import { useBoolean } from 'ahooks';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { v4 as generateUUID } from 'uuid';

import type { IAction, TourFrame, TourStep } from '@carboncopyco/types';
import {
  type DeviceType,
  type Dimension,
  IActionType,
  IEditorStatus,
  RecorderStatus,
  TourCaptureIncomingMessageType,
  TourCaptureOutgoingMessageType,
} from '@carboncopyco/types';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  CommunicationService,
  bindSnapshotHotkeys,
  desktopApi,
  isDesktop,
} from '@carboncopyco/mimesis-shared';
import {
  AnalyticsTrackEventName,
  Debug,
  useAnalytics,
} from '@carboncopyco/utils';
import { scopedLogger, Scope } from '@carboncopyco/logger';

import {
  selectOrganizationId,
  useCreateTourFrameMutation,
  useCreateTourMutation,
  useUpdateTourStatusMutation,
  useUpdateTourWithNewFramesMutation,
  useUploadToursFileMutation,
  useUploadToursResourceMutation,
} from '@carboncopyco/data';
import type { eventWithTime } from '@carboncopyco/rrweb-types';
import { ColorHex, LoadingDots, notify } from '@carboncopyco/ui';
import DimensionPanelWrapper from '../../components/DimensionPanelWrapper';
import { useTourDispatch, useTourSelector } from '../../hooks/store';
import { useProcessImageAsFrame } from '../../hooks/useProcessImageAsFrame';
import useTourCapture from '../../hooks/useTourCapture';
import {
  addTourFrame,
  resetTourFrames,
  selectIsCaptureSnapshotPending,
  selectIsCaptureStarted,
  selectIsCapturing,
  selectIsCapturingFinished,
  selectIsReadyToCapture,
  selectIsShowingDimensionPanel,
  selectTourId,
  setCanFinishCapturing,
  setIsCaptureButtonDisabled,
  setIsCaptureSnapshotPending,
  setIsCaptureStarted,
  setIsCapturing,
  setIsCapturingFinished,
  setIsReadyToCapture,
  setIsShowingDimensionPanel,
  setTourId,
  updateFramesLoading,
} from '../../store';
import { CAPTURE_IFRAME_ID } from '../../utils/constants';
import { getScreenshotBase64 } from '../../utils/getScreenshotBase64';
import { handleDimensionChange as handleDimensionChangeUtil } from '../../utils/recordUtils';
import { uploadAllResources } from '../../utils/toursResources';
import { useFinishCapturingActionsContext } from './FinishCapturingActionsContext';
import FirstFrameFailedCapturingModal from './FirstFrameFailedCapturingModal';
import { useTourCapturePageProps } from './TourCapturePageContext';
import { localise } from './localise';
import useDimensions from './useDimensions';

const debug = Debug.extend('TourCaptureDynamicRecorder');
const tourCaptureLogger = scopedLogger(Scope.TOUR_CAPTURE);

const DEMO_BACK_DELAY_MS = 200;

interface TourCaptureDynamicRecorderProps {
  setPageTitle: (pageTitle: string) => void;
  demo: any;
  originalTourId: string;
  tourEditorTabId?: number;
  insertIndex: number;
  webViewRef?: React.RefObject<HTMLElement>;
  onDimensionChange?: (
    dimension: Dimension,
    type: DeviceType,
    fitFactor: number
  ) => void;
}

const TourCaptureDynamicRecorder = ({
  setPageTitle,
  demo: { id: demoId, demoName, firstUrl, playerToken: token },
  originalTourId,
  tourEditorTabId,
  insertIndex,
  webViewRef,
  onDimensionChange,
}: TourCaptureDynamicRecorderProps) => {
  const {
    useNavigate,
    dynamicRecordingUrl,
    fileServiceDynamicLink,
    mimesisReplicateUrl,
    webViewFrame,
    desktopHooks,
    tab,
    uuid,
    activeTabId,
  } = useTourCapturePageProps();
  const { workspaceId } = useParams();
  const { targetWorkspaceId } = useFinishCapturingActionsContext();
  const { organizationId: orgId } = useParams<{
    organizationId: string;
  }>();
  const tabId = tab?.id;
  const tabManager = desktopHooks?.useTabManager();
  const communicationService = useMemo(
    () => new CommunicationService({ channelId: uuid }),
    [uuid]
  );

  const analytics = useAnalytics();
  const dispatch = useTourDispatch();
  const organizationId = useSelector(selectOrganizationId);
  const isCapturing = useTourSelector(selectIsCapturing(uuid));
  const isShowingDimensionPanel = useTourSelector(
    selectIsShowingDimensionPanel(uuid)
  );

  const isCapturingFinished = useTourSelector(selectIsCapturingFinished(uuid));
  const isCaptureStarted = useTourSelector(selectIsCaptureStarted(uuid));
  const [isRRWEBCaptureStarted, setIsRRWEBCaptureStarted] = useState(false);
  const isReadyToCapture = useTourSelector(selectIsReadyToCapture(uuid));
  const tourId = useTourSelector(selectTourId(uuid));
  const isCaptureSnapshotPending = useTourSelector(
    selectIsCaptureSnapshotPending(uuid)
  );

  const [recorderIframe, setRecorderIframe] = useState<HTMLIFrameElement>();
  const currentFrameIndex = useRef(0);
  const iframeRef = useRef(null);
  const [canRecord, setCanRecord] = useBoolean(true);
  const [createTour, { data: tourResponse }] = useCreateTourMutation();
  const [createTourFrame] = useCreateTourFrameMutation();
  const [uploadToursResource] = useUploadToursResourceMutation();
  const [uploadToursFile] = useUploadToursFileMutation();
  const [frameIdsCaptured, setFramesIdsCaptured] = useState<TourFrame['id'][]>(
    []
  );
  const [uploadedFilesSet, setUploadedFilesSet] = useState<Set<string>>(
    new Set()
  );
  const [visitedUrls, setVisitedUrls] = useState<string[]>([]);

  const [firstFrameTitle, setFirstFrameTitle] = useState<string>();
  const [didCaptureCrash, setDidCaptureCrash] = useState<boolean>(false);
  const [isPageNotAvailable, setIsPageNotAvailable] = useState<boolean>(false);
  const [isToursRecorderReady, setIsToursRecorderReady] =
    useState<boolean>(false);
  const [isEditorReady, setIsEditorReady] = useState<boolean>(false);
  const [isContinueTourRequested, setIsContinueTourRequested] =
    useState<boolean>(false);
  const [beforeUnloadDidHappen, setBeforeUnloadDidHappen] =
    useState<boolean>(false);
  const [firstFrameFailedCapturing, setFirstFrameFailedCapturing] =
    useState<boolean>(false);
  const [blobFilesToBeReplaced, setBlobFilesToBeReplaced] = useState<
    Set<{ base64: string; blobURI: string }>
  >(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const flags = useFlags();
  const { closeDimensionsPanel } = useDimensions({ uuid });

  const { uploadAnImage } = useProcessImageAsFrame({
    fileServiceDynamicLink,
  });

  const { stepConfigToInheritFrom } = useTourCapture({
    originalTourId,
  });

  const showLoadingDots = useMemo(() => {
    if (isPageNotAvailable) {
      return false;
    }
    return !canRecord || !isToursRecorderReady;
  }, [canRecord, isPageNotAvailable, isToursRecorderReady]);

  useEffect(() => {
    if (tourId) {
      tourCaptureLogger.setContextProperty('tourId', tourId);
    }
  }, tourId);

  const addToUploadedFilesSet = (fileName: string) => {
    setUploadedFilesSet((previousSet) => new Set(previousSet).add(fileName));
  };

  const addToBlobFilesToBeReplacedSet = (base64: string, blobURI: string) => {
    setBlobFilesToBeReplaced((previousSet) =>
      new Set(previousSet).add({ base64, blobURI })
    );
  };

  const removeFromUploadedFilesSet = (fileName: string) => {
    setUploadedFilesSet((previousSet) => {
      const newSet = new Set(previousSet);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      }
      return newSet;
    });
  };

  const clearUploadedFilesSet = () => {
    setUploadedFilesSet(new Set());
  };

  const sendSnapshotRequest = () => {
    communicationService.postMessage({
      message: {
        type: TourCaptureIncomingMessageType.requestSnapshot,
      },
      targetWindow: recorderIframe?.contentWindow,
    });
    tourCaptureLogger.info('[Clone]: Snapshot requested');
  };

  useEffect(() => {
    if (tourResponse) {
      debug({ tourResponse }, { recorderIframe });
      communicationService.postMessage({
        message: {
          type: TourCaptureIncomingMessageType.startTour,
          payload: {
            frameIndex: currentFrameIndex.current,
            listenSnapshotKeyboardEvents: true,
            shouldTakeScreenshots: flags.tourCaptureTakeScreenshots,
            uuid: uuid,
            tourId: tourResponse?.id,
            organizationId: orgId,
            fileServiceDynamicLink,
            tabId,
            isDesktop,
          },
        },
        targetWindow: recorderIframe?.contentWindow,
      });
      tourCaptureLogger.info('[Clone]: Capture started', {
        tourId: tourResponse.id,
        organizationId: orgId,
      });
      tourCaptureLogger.setContextProperty('tourId', tourResponse.id);

      setIsRRWEBCaptureStarted(true);

      bindSnapshotHotkeys(document, sendSnapshotRequest);

      if (tourResponse?.id) {
        dispatch(setTourId({ uuid, tourId: tourResponse?.id }));
        if (isDesktop && tabId) {
          window.dsDesktop.tour.startCapturing(tabId, {
            tourId: tourResponse.id,
            salt: tabId,
          });
        }
      }
    }
  }, [tourResponse, uuid]);

  useEffect(() => {
    if (recorderIframe?.contentWindow) {
      communicationService.postMessage({
        message: {
          type: TourCaptureIncomingMessageType.tourIsCapturing,
        },
        targetWindow: recorderIframe?.contentWindow,
      });
    }
  }, [recorderIframe?.contentWindow]);

  useEffect(() => {
    if (
      tourResponse &&
      isCaptureStarted &&
      firstFrameTitle &&
      !didCaptureCrash
    ) {
      dispatch(
        updateFramesLoading({
          uuid,
          frameLoading: {
            frameIndex: 0,
            isLoading: false,
          },
        })
      );
      dispatch(resetTourFrames({ uuid }));
    }
  }, [uuid, isCaptureStarted, tourResponse, firstFrameTitle, didCaptureCrash]);

  useEffect(() => {
    if (isCapturing) {
      setCanRecord.setFalse();
      setDidCaptureCrash(false);
      startTour();
    }

    if (!isCapturing && isCaptureStarted && isCapturingFinished) {
      endTour();
    }
    if (!isCapturing && isCaptureStarted && !isCapturingFinished) {
      restartTour();
    }
  }, [isCapturing]);

  useEffect(() => {
    communicationService.onMessage(onPostMessage);

    return () => {
      communicationService.removeMessageListener(onPostMessage);
    };
  }, [
    tourResponse,
    frameIdsCaptured,
    isCaptureStarted,
    beforeUnloadDidHappen,
    recorderIframe,
    isPageNotAvailable,
    uploadedFilesSet,
    visitedUrls,
    blobFilesToBeReplaced,
    activeTabId,
  ]);

  useEffect(() => {
    if (desktopApi?.editor && tab?.id) {
      desktopApi.editor.onMessage(onEditorPostMessage, tab.id);
      return () => desktopApi.editor.removeEventListeners(tab.id);
    }
  }, []);

  useEffect(() => {
    if (
      isContinueTourRequested &&
      isToursRecorderReady &&
      !isPageNotAvailable
    ) {
      if (isEditorReady || !isDesktop) {
        setIsEditorReady(false);
      }

      setIsContinueTourRequested(false);
      continueTour();
    }
  }, [
    isContinueTourRequested,
    isToursRecorderReady,
    isEditorReady,
    isPageNotAvailable,
  ]);

  useEffect(() => {
    function onLoad() {
      setIsToursRecorderReady(false);
      setIsContinueTourRequested(true);
    }

    if (
      recorderIframe &&
      isCapturing &&
      isCaptureStarted &&
      isRRWEBCaptureStarted
    ) {
      recorderIframe.onload = onLoad;
    }
  }, [recorderIframe, isCapturing, isCaptureStarted, isRRWEBCaptureStarted]);

  useEffect(() => {
    if (isCaptureSnapshotPending) {
      sendSnapshotRequest();
      dispatch(
        setIsCaptureSnapshotPending({
          uuid,
          isPending: false,
        })
      );
    }
  }, [isCaptureSnapshotPending, uuid]);

  const [updateTourStatus] = useUpdateTourStatusMutation();
  const [updateTourWithNewFrames] = useUpdateTourWithNewFramesMutation();
  const { navigateToTourEditor } = useNavigate();

  const sendPostMessageToDynamicRecorder = ({ type, payload }: IAction) => {
    const iframe = document.getElementById(
      'recorder-iframe'
    ) as HTMLIFrameElement;
    communicationService.postMessage({
      message: { type, payload },
      targetWindow: iframe?.contentWindow,
    });
  };

  const goToLastWorkingUrl = async () => {
    for await (const url of visitedUrls.reverse()) {
      const response = await fetch(url, {
        credentials: 'include',
      });
      if (response.status.toString().startsWith('2')) {
        recorderIframe.contentWindow.location.href = url;
        setVisitedUrls([]);
        break;
      }
    }
  };

  const onPostMessage = async (event) => {
    if (event.data) {
      const { data } = event;
      switch (data.status) {
        case TourCaptureOutgoingMessageType.isCapturingTour:
          communicationService.postMessage({
            message: {
              type: TourCaptureIncomingMessageType.tourIsCapturing,
            },
            targetWindow: recorderIframe?.contentWindow,
          });
          break;
        case IEditorStatus.editorLoaded: {
          debug('onPostMessage', data);
          sendPostMessageToDynamicRecorder({
            type: IActionType.startPreview,
            payload: { flags },
          });
          setIsEditorReady(true);
          break;
        }
        case TourCaptureOutgoingMessageType.DEMO_BACK: {
          setTimeout(() => {
            goToLastWorkingUrl();
          }, DEMO_BACK_DELAY_MS);
          if (frameIdsCaptured.length === 0) {
            break;
          }

          /*
            BRING BACK ONCE WE HAVE A API ENDPOINT FOR DELETING FRAMES
            deleteFrame({
              organizationId,
              id: frameIdsCaptured[frameIdsCaptured.length - 1],
            })
              .unwrap()
              .then(() => {
                setFramesIdsCaptured(prevFrames =>
                  prevFrames.slice(0, prevFrames.length - 1)
                )
                setCurrentFrameIndex(prevState => prevState - 1)
                dispatch(removeLastFrame())
              })
              .catch(() => notify.error("Error while deleting frame"))
          */

          setIsContinueTourRequested(true);
          setIsPageNotAvailable(false);

          break;
        }
        case TourCaptureOutgoingMessageType.URL_CHANGED: {
          debug('onPostMessage', data);
          if (!isDesktop && !recorderIframe) {
            const iframe = document.getElementById(
              'recorder-iframe'
            ) as HTMLIFrameElement;
            setRecorderIframe(iframe);
          }

          if (!isReadyToCapture) {
            dispatch(setIsReadyToCapture({ uuid }));
          } else if (isDesktop && isCaptureStarted && isCapturing) {
            setIsContinueTourRequested(true);
          }

          if (beforeUnloadDidHappen) {
            setBeforeUnloadDidHappen(false);
          }

          setVisitedUrls((urls) => [...urls, data.payload.proxyUrl]);
          break;
        }
        case TourCaptureOutgoingMessageType.BEFORE_UNLOAD: {
          if (isCaptureStarted && isCapturing) {
            setBeforeUnloadDidHappen(true);
          }
          break;
        }
        case TourCaptureOutgoingMessageType.fileUpload: {
          if (isCaptureStarted) {
            debug('onPostMessage', data);
            const frameIndex = currentFrameIndex.current;
            currentFrameIndex.current += 1;
            await onNewChunk({
              frameIndex,
              data: data.payload,
            });
            tourCaptureLogger.info('[Clone]: Frame uploaded', { frameIndex });
          }
          break;
        }
        case TourCaptureOutgoingMessageType.mimesisReplicateStatus: {
          setIsToursRecorderReady(data.payload.mimesisReplicateReady);
          break;
        }
        case TourCaptureOutgoingMessageType.recordingInProgress: {
          debug('onPostMessage', data);
          setFirstFrameTitle(data.payload.pageTitle);
          setCanRecord.setTrue();
          break;
        }
        case TourCaptureOutgoingMessageType.updatePageTitle: {
          debug('onPostMessage', data);
          setPageTitle(data.title);
          break;
        }
        case TourCaptureOutgoingMessageType.PAGE_NOT_AVAILABLE: {
          communicationService.postMessage({
            message: {
              type: TourCaptureIncomingMessageType.isTour,
            },
            targetWindow: recorderIframe?.contentWindow,
          });
          dispatch(
            setCanFinishCapturing({
              uuid,
              canFinish: false,
            })
          );
          setIsPageNotAvailable(true);
          break;
        }
        case TourCaptureOutgoingMessageType.blobToBase64ConversionResult: {
          const { base64, blobURI } = data.payload;
          addToBlobFilesToBeReplacedSet(base64, blobURI);
          break;
        }
      }
    }
  };

  const onEditorPostMessage = (event) => {
    if (event.data.status === IEditorStatus.editorLoaded) {
      debug('onPostMessage', event);
      desktopApi.editor.postMessage(
        {
          type: IActionType.startPreview,
          payload: { flags: { ...flags } },
        },
        tab.id
      );
      setIsEditorReady(true);
    }
  };

  const startTour = async () => {
    dispatch(setIsCaptureStarted({ uuid, isStarted: true }));
    await createTour({
      organizationId,
      demoId,
      tourName: `Tour for ${demoName}`,
      description: `This tour is for ${demoName}`,
      source: isDesktop ? 'desktop' : 'platform',
    });
    dispatch(setCanFinishCapturing({ uuid, canFinish: true }));
  };
  const continueTour = () => {
    communicationService.postMessage({
      message: {
        type: TourCaptureIncomingMessageType.continueTour,
        payload: {
          frameIndex: currentFrameIndex.current,
          listenSnapshotKeyboardEvents: true,
          tourId: tourResponse?.id,
          organizationId: orgId,
          fileServiceDynamicLink,
          tabId,
          isDesktop,
        },
      },
      targetWindow: recorderIframe?.contentWindow,
    });
    tourCaptureLogger.info('[Clone]: Capture continued');
    dispatch(setCanFinishCapturing({ uuid, canFinish: true }));
    if (isPageNotAvailable) {
      setIsPageNotAvailable(false);
    }
  };
  const endTour = async () => {
    communicationService.postMessage({
      message: {
        type: TourCaptureIncomingMessageType.endTour,
      },

      targetWindow: recorderIframe?.contentWindow,
    });
    tourCaptureLogger.info('[Clone]: Capture ended');
  };

  const restartTour = async () => {
    clearUploadedFilesSet();
    dispatch(setIsCaptureStarted({ uuid, isStarted: false }));
    if (isPageNotAvailable) {
      communicationService.postMessage({
        message: {
          type: TourCaptureIncomingMessageType.pageGoBack,
        },
        targetWindow: recorderIframe?.contentWindow,
      });
      tourCaptureLogger.info('[Clone]: Page not available, going back');
    }

    communicationService.postMessage({
      message: {
        type: TourCaptureIncomingMessageType.restartTour,
      },
      targetWindow: recorderIframe?.contentWindow,
    });
    tourCaptureLogger.info('[Clone]: Capture restarted');
    communicationService.postMessage({
      message: { type: RecorderStatus.RECORDER_RELOAD },
      targetWindow: recorderIframe?.contentWindow,
    });
    dispatch(setCanFinishCapturing({ uuid, canFinish: true }));
    if (isPageNotAvailable) {
      setIsPageNotAvailable(false);
    }
    currentFrameIndex.current = 0;
  };

  const handleRestartCapturingAfterFirstFrameFailedCapturing = () => {
    setFirstFrameFailedCapturing(false);
    dispatch(
      setIsCaptureButtonDisabled({
        uuid,
        disabled: false,
      })
    );
    dispatch(setIsCapturing({ uuid, isCapturing: true }));
    dispatch(
      setIsCapturingFinished({
        uuid,
        isCapturingFinished: false,
      })
    );
    dispatch(setIsCaptureStarted({ uuid, isStarted: true }));
  };

  const handleFirstFrameFailedCapturing = (isFirstFrame: boolean) => {
    if (isFirstFrame) {
      dispatch(setIsCaptureButtonDisabled({ uuid, disabled: true }));
      dispatch(setIsCapturing({ uuid, isCapturing: false }));
      dispatch(setIsCaptureStarted({ uuid, isStarted: false }));
      dispatch(resetTourFrames({ uuid }));
      dispatch(setIsReadyToCapture({ uuid }));
      setDidCaptureCrash(true);
      return setFirstFrameFailedCapturing(true);
    }
    return notify.error('Sorry, frame was not created');
  };

  const onNewChunk = async ({
    data,
    frameIndex,
  }: {
    data: any;
    frameIndex: number;
  }) => {
    if (data?.frameData) {
      const events: eventWithTime[] = data.frameData;
      const defaultStep = data.partialNewFrameData.steps[0] as TourStep;
      const originalLayout = defaultStep.config.tooltip.balloon.layout;

      if (stepConfigToInheritFrom) {
        defaultStep.config = {
          ...stepConfigToInheritFrom,
          tooltip: {
            ...stepConfigToInheritFrom.tooltip,
            balloon: {
              ...stepConfigToInheritFrom.tooltip.balloon,
              layout: originalLayout,
            },
          },
        };
      }

      const defaultSteps = [
        {
          ...defaultStep,
          title: 'Untitled step',
          stepId: generateUUID(),
        },
      ];

      let newFrameData = {
        ...data.partialNewFrameData,
        screenshotBase64: await getScreenshotBase64(
          flags.tourCaptureTakeScreenshots,
          Number.parseInt(uuid)
        ),
        frameDataUrl: '',
        tourId: tourResponse?.id,
        frameIndex,
        steps: defaultSteps,
        organizationId,
      };
      if (!data.isLast) {
        dispatch(addTourFrame({ uuid, tourFrame: newFrameData }));
        dispatch(
          updateFramesLoading({
            uuid,
            frameLoading: {
              frameIndex,
              isLoading: true,
            },
          })
        );
      }

      const { compressedEvents, downloadedResourcesArray } = await localise({
        events,
        organizationId,
        tourId: tourResponse.id,
        blobFilesToBeReplaced,
        fileServiceDynamicLink,
        tabId,
        errorCallback: (error) => {
          analytics.trackEvent(AnalyticsTrackEventName.MIMESIS_SDK_ERROR, {
            tourId,
            functionName: 'getCompressedEvents',
            error,
          });
        },
      });

      const fileServiceToken = tourResponse?.fileServiceToken;

      uploadToursFile({
        organizationId,
        tourId: tourResponse?.id,
        jsonId: generateUUID(),
        token: fileServiceToken,
        frames: compressedEvents,
      })
        .unwrap()
        .then(async (tourFileUploadResponse) => {
          const urlToJson = tourFileUploadResponse?.url_to_json;

          const imgPath = await uploadAnImage(
            newFrameData.screenshotBase64,
            tourResponse?.id,
            fileServiceToken
          );

          newFrameData = {
            ...newFrameData,
            pageUrl: urlToJson,
            frameDataUrl: urlToJson,
            frameScreenshotUrl: imgPath,
          };

          createTourFrame(newFrameData)
            .unwrap()
            .then((frameResponse) => {
              setFramesIdsCaptured((prevFrames) => [
                ...prevFrames,
                frameResponse.id,
              ]);
              if (!isDesktop) {
                uploadAllResources({
                  resources: downloadedResourcesArray,
                  tourId: tourResponse?.id,
                  fileServiceToken: tourResponse?.fileServiceToken,
                  organizationId,
                  uploadToursResource,
                  uploadedFilesSet,
                  addToUploadedFilesSet,
                  removeFromUploadedFilesSet,
                  onProgress: (progress) => {
                    dispatch(
                      updateFramesLoading({
                        uuid,
                        frameLoading: {
                          frameIndex,
                          progress,
                          isLoading: true,
                        },
                      })
                    );
                  },
                }).then(() => {
                  dispatch(
                    updateFramesLoading({
                      uuid,
                      frameLoading: {
                        frameIndex,
                        isLoading: false,
                      },
                    })
                  );
                });
              } else {
                dispatch(
                  updateFramesLoading({
                    uuid,
                    frameLoading: {
                      frameIndex,
                      isLoading: false,
                    },
                  })
                );
              }
              if (data.isLast) {
                if (originalTourId) {
                  updateTourWithNewFrames({
                    organizationId,
                    id: Number.parseInt(originalTourId),
                    newTourId: tourId,
                    insertIndex: insertIndex,
                  })
                    .unwrap()
                    .then(() => {
                      communicationService.removeMessageListener(onPostMessage);
                      tabManager?.closeGlobalActiveTab?.();
                      navigateToTourEditor({
                        id: originalTourId,
                        selectedFrameIndex: insertIndex,
                        tourEditorTabId,
                        skipVisibilityCheck: true,
                        workspaceId: targetWorkspaceId.current || workspaceId,
                      });
                    })
                    .catch(() => {
                      notify.error('Error adding new frames to the tour');
                    });

                  return;
                }

                updateTourStatus({
                  organizationId,
                  id: tourId,
                  status: 'recording_finished',
                })
                  .unwrap()
                  .then(() => {
                    communicationService.removeMessageListener(onPostMessage);
                    tabManager?.closeGlobalActiveTab?.();
                    navigateToTourEditor({
                      id: tourId,
                      workspaceId: targetWorkspaceId.current || workspaceId,
                      tourEditorTabId,
                    });
                  })
                  .catch(() => {
                    notify.error('Error updating tour status to complete');
                  });
              }
            })
            .catch(() => {
              dispatch(
                updateFramesLoading({
                  uuid,
                  frameLoading: {
                    frameIndex,
                    isLoading: false,
                  },
                })
              );
              handleFirstFrameFailedCapturing(frameIndex === 0);
            });
        })
        .catch(() => {
          handleFirstFrameFailedCapturing(frameIndex === 0);
        });
    }
  };

  const handleDimensionChange = useCallback(
    (dimension) => {
      const { width, height, type } = dimension;

      const { fitFactor } = handleDimensionChangeUtil(
        { width: width, height: height },
        containerRef?.current
      );

      onDimensionChange?.({ width, height }, type, fitFactor);

      analytics.trackEvent(
        AnalyticsTrackEventName.TOUR_SELECT_DIMENSIONS_SETUP_DEVICE,
        {
          location: 'tour_capture_navbar_demo_based',
          checkedItem: type,
          tourId,
          timestamp: Date.now(),
        }
      );
    },
    [analytics, onDimensionChange, tourId]
  );

  const showWebView = webViewFrame && !firstFrameFailedCapturing;

  return (
    <div
      ref={containerRef}
      className="h-full w-full relative"
    >
      {(iframeRef || webViewRef) && (
        <DimensionPanelWrapper
          iframeEl={iframeRef?.current}
          anchorRef={isDesktop ? webViewRef : iframeRef}
          visible={isShowingDimensionPanel}
          offset={[-20, -580]}
          containerRef={containerRef}
          activeTabId={activeTabId}
          onClose={closeDimensionsPanel}
          onSubmit={(dimension) => {
            dispatch(
              setIsShowingDimensionPanel({
                uuid,
                isShowingDimensionPanel: !isShowingDimensionPanel,
              })
            );

            analytics.trackEvent(
              AnalyticsTrackEventName.TOUR_CLICK_DIMENSIONS_SETUP_APPLY,
              {
                location: 'tour_capture_navbar_live',
                checkedItem: dimension.type,
                tourId,
                timestamp: Date.now(),
              }
            );
          }}
          onDimensionChange={handleDimensionChange}
        />
      )}
      {showLoadingDots && (
        <div className="h-full w-full absolute flex justify-center items-center bg-ds-accent3-100 cursor-not-allowed">
          <LoadingDots color={ColorHex.natural600} />
        </div>
      )}
      {firstFrameFailedCapturing && (
        <FirstFrameFailedCapturingModal
          anchorRef={containerRef}
          open={firstFrameFailedCapturing}
          onConfirm={handleRestartCapturingAfterFirstFrameFailedCapturing}
        />
      )}
      {showWebView && webViewFrame}
      {!webViewFrame && (
        <iframe
          ref={iframeRef}
          title="demostack-iframe"
          className="w-full h-full"
          name="demostack-iframe"
          id={CAPTURE_IFRAME_ID}
          src={`${dynamicRecordingUrl}?${
            mimesisReplicateUrl && !window.__DS_TOUR_IN_TOUR
              ? `mimesis_replicate=${mimesisReplicateUrl}&`
              : ''
          }demosgw_token=${token}&first_url=${firstUrl}&analytics_session_id=${analytics.userData.anonymousId()}`}
        />
      )}
    </div>
  );
};

export default TourCaptureDynamicRecorder;
